// Browser-runnable port of @archlang/server's widgets-css generator.
// Walks every string-form `widget:` template in the resolved model,
// extracts Tailwind-shaped class candidates, runs UnoCSS over them,
// returns the CSS. Identical logic to server-side; only the bundling
// substrate is different (no Node deps).
//
// `applyCascade` should already have been called on the model — we
// look at instance widget fields after cascade resolves them.

// @ts-ignore — UnoCSS's TS generics couple to the inline theme shape;
// the runtime API is more permissive than the static signature.
import { createGenerator } from "@unocss/core";
// @ts-ignore
import presetWind from "@unocss/preset-wind";
import type {
  Body,
  InterfaceBody,
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
  Value,
} from "@archlang/engine";

// Colours resolve to the shared theme tokens (CSS variables defined in
// @archlang/app-shell/theme.css), so widget templates re-theme with the
// rest of the UI when `data-theme` flips.
const archTheme = {
  colors: {
    arch: {
      50: "rgb(var(--arch-50))",
      100: "rgb(var(--arch-100))",
      200: "rgb(var(--arch-200))",
      300: "rgb(var(--arch-300))",
      400: "rgb(var(--arch-400))",
      500: "rgb(var(--arch-500))",
      600: "rgb(var(--arch-600))",
      700: "rgb(var(--arch-700))",
      800: "rgb(var(--arch-800))",
      900: "rgb(var(--arch-900))",
      950: "rgb(var(--arch-950))",
    },
  },
  boxShadow: {
    "arch-glow": "var(--shadow-glow)",
    "arch-glow-lg": "var(--shadow-glow-lg)",
    "arch-card": "var(--shadow-card)",
  },
};

const archRules: Array<[string, Record<string, string>]> = [
  ["bg-arch-card", { "background-image": "var(--gradient-card)" }],
  ["bg-arch-card-hot", { "background-image": "var(--gradient-card-hot)" }],
];

type Generator = { generate(input: string, opts?: unknown): Promise<{ css: string }> };
let generatorPromise: Promise<Generator> | undefined;
function getGenerator(): Promise<Generator> {
  if (!generatorPromise) {
    const config: unknown = {
      presets: [presetWind()],
      theme: archTheme,
      rules: archRules,
    };
    generatorPromise = (createGenerator as unknown as (c: unknown) => Promise<Generator>)(config);
  }
  return generatorPromise;
}

const CLASS_TOKEN_RE = /[A-Za-z][A-Za-z0-9:_/\-.[\]%#]*/g;

let cachedKey = "";
let cachedCss = "";

export async function generateWidgetsCss(model: ResolvedModel): Promise<string> {
  const classes = new Set<string>();
  for (const m of model.modules) collectFromModule(m, classes);
  for (const t of model.types) {
    if (t.body) collectFromBody(t.body, classes);
  }

  const sorted = [...classes].sort();
  const key = sorted.join(" ");
  if (key === cachedKey) return cachedCss;

  const generator = await getGenerator();
  const result = await generator.generate(key, { preflights: false });
  cachedKey = key;
  cachedCss = result.css;
  return cachedCss;
}

export function _resetWidgetsCssCache(): void {
  cachedKey = "";
  cachedCss = "";
}

function collectFromModule(m: ResolvedModule, into: Set<string>): void {
  collectFromBody(m.body, into);
  for (const f of m.surfaces) collectFromSurface(f, into);
  for (const i of m.interfaces) collectFromInterface(i, into);
  for (const child of m.modules) collectFromModule(child, into);
}

function collectFromSurface(f: ResolvedSurface, into: Set<string>): void {
  collectFromBody(f.body, into);
  for (const sub of f.surfaces) collectFromSurface(sub, into);
  for (const i of f.interfaces) collectFromInterface(i, into);
}

function collectFromInterface(i: ResolvedInterface, into: Set<string>): void {
  collectFromInterfaceBody(i.body, into);
}

function collectFromBody(body: Body, into: Set<string>): void {
  for (const f of body.fields) {
    const text = templateTextFromField(f);
    if (text) extractInto(text, into);
  }
}

function collectFromInterfaceBody(body: InterfaceBody, into: Set<string>): void {
  for (const f of body.fields) {
    const text = templateTextFromField(f);
    if (text) extractInto(text, into);
  }
}

function templateTextFromField(f: { path: readonly string[]; value?: Value }): string | undefined {
  if (f.path.length !== 1 || f.path[0] !== "widget") return undefined;
  if (!f.value) return undefined;
  return f.value.kind === "string" ? f.value.text : undefined;
}

function extractInto(source: string, into: Set<string>): void {
  let m: RegExpExecArray | null;
  CLASS_TOKEN_RE.lastIndex = 0;
  while ((m = CLASS_TOKEN_RE.exec(source)) !== null) {
    into.add(m[0]);
  }
}
